import { useTranslation } from 'react-i18next';
import { Download, FileText } from 'lucide-react';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ApiSupportMessage } from '@/shared/types/api/support-chat';

type Props = {
  message: ApiSupportMessage;
};

const formatSize = (bytes: number | null): string => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FeaturesSupportChatMessageBubble = ({ message }: Props) => {
  const { t } = useTranslation();
  const isStaff = message.sender_type === 'STAFF';

  return (
    <div className={cn('flex w-full', isStaff ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'flex max-w-[80%] flex-col gap-1 rounded-2xl px-3 py-2 text-sm shadow-sm sm:max-w-[70%]',
          isStaff
            ? 'rounded-br-sm bg-brand-600 text-white'
            : 'rounded-bl-sm bg-white text-ink border border-slate-200',
        )}
      >
        {message.file && message.file_type === 'IMAGE' && (
          <a href={message.file} target="_blank" rel="noreferrer" className="block">
            <img
              src={message.file}
              alt={message.file_name ?? ''}
              className="max-h-64 w-auto rounded-lg object-cover"
            />
          </a>
        )}
        {message.file && message.file_type === 'FILE' && (
          <a
            href={message.file}
            target="_blank"
            rel="noreferrer"
            download={message.file_name ?? undefined}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2 py-1.5',
              isStaff ? 'bg-white/15 hover:bg-white/25' : 'bg-slate-100 hover:bg-slate-200',
            )}
          >
            <FileText className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {message.file_name ?? t('support.chat.file')}
            </span>
            <span className={cn('text-xs', isStaff ? 'text-white/70' : 'text-ink-muted')}>
              {formatSize(message.file_size)}
            </span>
            <Download className="h-4 w-4 shrink-0" aria-hidden />
          </a>
        )}
        {message.text && <p className="whitespace-pre-wrap break-words">{message.text}</p>}
        <span className={cn('self-end text-[10px]', isStaff ? 'text-white/70' : 'text-ink-muted')}>
          {formatDateTime(message.created_at)}
        </span>
      </div>
    </div>
  );
};
